import axios from 'axios';
import SessionService from './SessionService';
import { config } from '../config';
import type { PagedResult, ScheduleSummaryDTO } from '../types/pagedAndDtos';

const API_URL = config.API_BASE_URL;

export interface Attachment {
  type: 'image' | 'audio' | 'video' | 'file';
  data: string;
  name: string;
  mimeType?: string;
}

export interface MessageSchedulingData {
  contact_id: number;
  message: string;
  scheduled_at: string;
  attachments?: Attachment[];
}

export interface RecipientDTO {
  recipientId: string;
  recipientType: 'Contact' | 'Group';
  recipientName?: string;
}

export interface MessageScheduling {
  id: number;
  name: string;
  messageText: string;
  content?: string;
  sendDate?: string;
  scheduledDate?: string;
  flowId?: number;
  sectorId: number;
  userId?: number;
  tagIds?: string;
  imageName?: string;
  fileName?: string;
  imageAttachment?: string;
  fileAttachment?: string;
  imageMimeType?: string;
  fileMimeType?: string;
  status: string | boolean;
  attachments?: Array<{
    id: number;
    fileName: string;
    fileUrl: string;
    fileType: string;
    fileSize: number;
    messageId: number;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
  contactId?: number;
  contact_id?: number;
  location?: string;
  timezone?: string;
  recurrenceType: number;
  recurrenceInterval: number;
  recurrenceEndDate?: string;
  recurrenceWeekdays?: string;
  recurrenceMonthDays?: string;
  nextExecutionDate?: string;
  executionCount: number;
  audioUrl?: string;
  recipients: RecipientDTO[];
}

// DTO para criar um MessageScheduling
export interface CreateMessageSchedulingDTO {
  name: string;
  messageText: string;
  sendDate: string;
  contactId?: number; // Optional now if recipients used
  sectorId: number;
  status?: boolean;
  tagIds: string;
  attachments?: Attachment[];
  location?: string;
  timezone?: string;
  recurrenceType?: number;
  recurrenceInterval?: number;
  recurrenceEndDate?: string;
  recurrenceWeekdays?: string;
  recurrenceMonthDays?: string;
  audioUrl?: string;
  recipients?: RecipientDTO[];
}

// DTO para atualizar um MessageScheduling
export interface UpdateMessageSchedulingDTO {
  name?: string;
  messageText?: string;
  sendDate?: string;
  contactId?: number;
  sectorId?: number;
  status?: boolean;
  tagIds: string;
  attachments?: Attachment[];
  location?: string;
  timezone?: string;
  recurrenceType?: number;
  recurrenceInterval?: number;
  recurrenceEndDate?: string;
  recurrenceWeekdays?: string;
  recurrenceMonthDays?: string;
  audioUrl?: string;
  recipients?: RecipientDTO[];
}

/** Lista paginada de agendamentos (DTO enxuto) - use em telas com muitos registros. */
export const getMessageSchedulingsPaged = async (
  sectorId: number,
  pageNumber: number = 1,
  pageSize: number = 50
): Promise<PagedResult<ScheduleSummaryDTO>> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('No token available');

  const response = await axios.get<PagedResult<ScheduleSummaryDTO>>(
    `${API_URL}/message-schedulings/paged`,
    {
      params: { sectorId, pageNumber, pageSize },
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
    }
  );
  return response.data;
};

// Função para obter todos os MessageSchedulings
export const getMessageSchedulings = async (): Promise<MessageScheduling[]> => {
  const sectorId = SessionService.getSectorId();
  if (!sectorId) {
    throw new Error('No sector selected');
  }

  const token = SessionService.getToken();
  if (!token) {
    throw new Error('No token available');
  }

  try {
    const response = await axios.get(`${API_URL}/message-schedulings`, {
      params: {
        sectorId: sectorId
      },
      headers: {
        'Accept': '*/*',
        'Authorization': `Bearer ${token}`,
      },
    });

    // Se a resposta for 404 com a mensagem específica, retornar array vazio
    if (response.data.code === '404' && response.data.message === 'No message schedulings found.') {
      return [];
    }

    return response.data.data || [];
  } catch (error: any) {
    // Se for erro 404, retornar array vazio
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

// Função para obter um MessageScheduling pelo ID
export const getMessageScheduling = async (id: number): Promise<MessageScheduling> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('No token available');
  }

  try {
    const response = await axios.get(`${API_URL}/message-schedulings/${id}`, {
      headers: {
        'Accept': '*/*',
        'Authorization': `Bearer ${token}`,
      },
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(`Failed to get message scheduling with id ${id}: ${error.response?.data?.message || error.message}`);
  }
};

export interface UploadedSchedAttachment {
  url: string;
  name: string;
  mimeType: string;
  type: 'image' | 'audio' | 'video' | 'file' | string;
}

/**
 * Uploads media to sched-temp/ before create/update.
 * Avoids multi-MB base64 in the JSON body (common cause of axios "Network Error").
 */
export const uploadMessageSchedulingAttachment = async (
  file: File | Blob,
  sectorId: number,
  type: 'image' | 'audio' | 'video' | 'file',
  scheduleId?: number
): Promise<UploadedSchedAttachment> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('No token available');
  }

  const formData = new FormData();
  const fileName =
    file instanceof File && file.name
      ? file.name
      : `attachment_${Date.now()}.${type === 'image' ? 'jpg' : type === 'video' ? 'mp4' : type === 'audio' ? 'webm' : 'bin'}`;
  formData.append('file', file, fileName);
  formData.append('sectorId', String(sectorId));
  formData.append('type', type);
  if (scheduleId && scheduleId > 0) {
    formData.append('scheduleId', String(scheduleId));
  }

  try {
    const response = await axios.post(`${API_URL}/message-schedulings/attachments/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        // Let the browser set multipart boundary — do not force Content-Type.
      },
      timeout: 120_000,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
    return response.data as UploadedSchedAttachment;
  } catch (error: any) {
    throw new Error(
      `Failed to upload schedule attachment: ${error.response?.data?.message || error.message}`
    );
  }
};

// Função para criar um novo MessageScheduling
export const createMessageScheduling = async (
  messageData: CreateMessageSchedulingDTO
): Promise<MessageScheduling> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('No token available');
  }

  try {
    const response = await axios.post(`${API_URL}/message-schedulings`, messageData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
      timeout: 60_000,
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(`Failed to create message scheduling: ${error.response?.data?.message || error.message}`);
  }
};

// Função para atualizar um MessageScheduling existente
export const updateMessageScheduling = async (
  id: number,
  data: UpdateMessageSchedulingDTO
): Promise<MessageScheduling> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('No token available');
  }

  try {
    const response = await axios.put(`${API_URL}/message-schedulings/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(`Failed to update message scheduling with id ${id}: ${error.response?.data?.message || error.message}`);
  }
};

// Função para deletar um MessageScheduling
export const deleteMessageScheduling = async (id: number): Promise<void> => {
  const token = SessionService.getToken();
  if (!token) {
    throw new Error('No token available');
  }

  try {
    await axios.delete(`${API_URL}/message-schedulings/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
    });
  } catch (error: any) {
    throw new Error(`Failed to delete message scheduling with id ${id}: ${error.response?.data?.message || error.message}`);
  }
};

export class MessageSchedulingsService {
  static async create(data: MessageSchedulingData) {
    const sectorId = SessionService.getSectorId();
    if (!sectorId) throw new Error('Setor não selecionado');

    const token = SessionService.getToken();
    if (!token) throw new Error('Token não disponível');

    return axios.post(`${API_URL}/message-schedulings`, {
      ...data,
      sector_id: sectorId
    }, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': '*/*',
      },
    });
  }

  static async list() {
    const sectorId = SessionService.getSectorId();
    if (!sectorId) throw new Error('Setor não selecionado');

    const token = SessionService.getToken();
    if (!token) throw new Error('Token não disponível');

    return axios.get(`${API_URL}/message-schedulings?sector_id=${sectorId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
    });
  }

  static async delete(id: number) {
    const token = SessionService.getToken();
    if (!token) throw new Error('Token não disponível');

    return axios.delete(`${API_URL}/message-schedulings/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': '*/*',
      },
    });
  }
}
